/**
 * This is a benchmark report slice that contains the state and reducers for the benchmark report.
 * It should contain an async thunk to fetch the benchmark report data.
 */

import { fetchBenchmarkReportData, fetchScaledCutlines, GetLatestAssessmentsParams, GetScaledCutlinesParams } from "@/services/query/benchmarkReportAPI";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import benchmarkMockedData from "./benchmarkMockedData";
import { AsyncLoadingState } from "@/features/dashboard/enums";

// state interface for the benchmark report slice

export interface BenchmarkReportResponse {
  [key: string]: any;
}

export interface ScaledCutline {
  cutoffLabel: string;
  percentile: number | null;
  value: number;
}

export interface BenchmarkReportObject {
  benchmarkData: {
    scores: {
      student: {
        id: string;
        firstName: string;
        lastName: string;
        grade: string;
        classroomId: string;
        schoolId: string;
        metadata: {
          esl: boolean;
          earlyReader: boolean;
          spanishEarlyReader: boolean;
          saxon: boolean;
          tutorInSpanish: boolean;
          tutorEnabled: boolean;
          assessmentEnabled: boolean;
          assessmentLanguageOverride: string;
          screenerEnabled: boolean;
        };
      };
      school: string;
      classroom: string;
      scoreValue: number;
      displayScore: string;
      level: string;
      grl: string;
      labeledScore: string;
      status: string;
      pr: string;
      isPending: boolean;
      studentScores: {
        areaScore: number;
        areaDisplayScore: string;
        areaLabeledScore: string;
        esriScore: number;
        esriDisplayScore: string;
        esriLabeledScore: string;
        phonAwareScore: number;
        phonAwareDisplayScore: string;
        phonAwareLabeledScore: string;
        vocabSize: number;
        vocabDisplayScore: string;
        vocabLabeledScore: string;
        wcpmScore: number;
        wcpmDisplayScore: string;
        wcpmLabeledScore: string;
        equatedWcpm: number;
        equatedWcpmDisplayScore: string;
        equatedWcpmLabeledScore: string;
        decodeScore: number;
        decodeDisplayScore: string;
        decodeLabeledScore: string;
        armScore: number;
        armDisplayScore: string;
        armLabeledScore: string;
      };
    }[];
    cutlines: {
      id: string;
      percentile: number;
      value: number;
    }[];
    displayCutoffs: {
      poor: number;
      average: number;
      good: number;
    };
    cutoffs: {
      poor: number;
      average: number;
      good: number;
    };
  };
}
interface BenchmarkReportState {
  data: BenchmarkReportResponse | null;
  scaledCutlines: ScaledCutline[] | null;
  status: AsyncLoadingState;
  cutlinesStatus: AsyncLoadingState;
  error: string | null;
  cutlinesError: string | null;
}

// initial state for the benchmark report slice
const initialState: BenchmarkReportState = {
  data: null,
  scaledCutlines: null,
  status: AsyncLoadingState.NONE,
  cutlinesStatus: AsyncLoadingState.NONE,
  error: null,
  cutlinesError: null,
};

export const fetchBenchmarkReport = createAsyncThunk<BenchmarkReportResponse, GetLatestAssessmentsParams>(
  'benchmarkReport/fetchBenchmarkReport', 
  async ({studentIds, equatedScores = false, locale = 'EN_US', periodId, screeningWindow, testType}, {rejectWithValue}) => {
    try {
      const response = await fetchBenchmarkReportData({ studentIds, locale, equatedScores, periodId, screeningWindow, testType }) as BenchmarkReportResponse;
      return response;
    } catch (error: any) {
      console.error('Error fetching benchmark report data', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBenchmarkCutlines = createAsyncThunk<ScaledCutline[], GetScaledCutlinesParams>(
  'benchmarkReport/fetchBenchmarkCutlines',
  async (params, {rejectWithValue}) => {
    try {
      const response = await fetchScaledCutlines(params) as ScaledCutline[];
      return response;
    } catch (error: any) {
      console.error('Error fetching benchmark cutlines data', error);
      return rejectWithValue(error.message);
    }
  }
);

const benchmarkReportSlice = createSlice({
  name: 'benchmarkReport',
  initialState: initialState,
  reducers: {
    resetBenchmarkReport: (state) => {
      state.data = null;
      state.scaledCutlines = null;
      state.status = AsyncLoadingState.NONE;
      state.cutlinesStatus = AsyncLoadingState.NONE;
      state.error = null;
      state.cutlinesError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBenchmarkReport.pending, (state) => {
        state.status = AsyncLoadingState.LOADING;
      })
      .addCase(fetchBenchmarkReport.fulfilled, (state, action: PayloadAction<BenchmarkReportResponse>) => {
        state.status = AsyncLoadingState.SUCCESS;
        state.data = action.payload;
      })
      .addCase(fetchBenchmarkReport.rejected, (state, action) => {
        state.status = AsyncLoadingState.ERROR;
        state.error = action.error.message as string;
      })
      
      // Handle fetchBenchmarkCutlines states
      .addCase(fetchBenchmarkCutlines.pending, (state) => {
        state.cutlinesStatus = AsyncLoadingState.LOADING;
      })
      .addCase(fetchBenchmarkCutlines.fulfilled, (state, action: PayloadAction<ScaledCutline[]>) => {
        state.cutlinesStatus = AsyncLoadingState.SUCCESS;
        state.scaledCutlines = action.payload;
      })
      .addCase(fetchBenchmarkCutlines.rejected, (state, action) => {
        state.cutlinesStatus = AsyncLoadingState.ERROR;
        state.cutlinesError = action.error.message as string;
      });
  },
});

export const { } = benchmarkReportSlice.actions;
export const selectBenchmarkReport = (state: any) => state.benchmarkReport;
export const selectBenchmarkReportData = (state: any) => state.benchmarkReport.data;
export const selectBenchmarkReportStatus = (state: any) => state.benchmarkReport.status;
export const selectBenchmarkReportError = (state: any) => state.benchmarkReport.error;
export const selectBenchmarkCutlines = (state: any) => state.benchmarkReport.scaledCutlines;
export const selectBenchmarkCutlinesStatus = (state: any) => state.benchmarkReport.cutlinesStatus;
export const selectBenchmarkCutlinesError = (state: any) => state.benchmarkReport.cutlinesError;
export const { resetBenchmarkReport } = benchmarkReportSlice.actions;
export default benchmarkReportSlice.reducer;

